'use client'

import type { ReactNode } from 'react'
import { ChartNoAxesColumn, Briefcase, FileText, User } from 'lucide-react'

import { PageHeaderStack } from '@/components/navigation/page-header-stack'
import { PageLineTabs, type PageLineTabItem } from '@/components/navigation/page-line-tabs'
import { UnavailableLineTabTrigger } from '@/components/navigation/unavailable-line-tab-trigger'
import { Card, CardContent } from '@/components/ui/card'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import type { PageBreadcrumbItem } from '@/components/navigation/page-breadcrumbs'
import type {
  OpportunityMatchDetailTab,
  PersonMatchDetailTab,
} from '@/lib/match-detail-paths'

const ANALYSIS_UNAVAILABLE_TITLE = 'Match analysis is not available for this result yet.'

function AnalysisUnavailablePanel({ retry }: { retry?: ReactNode }) {
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-3 py-6">
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
          {ANALYSIS_UNAVAILABLE_TITLE}
        </p>
        {retry}
      </CardContent>
    </Card>
  )
}

type MatchDetailShellBaseProps = {
  title: string
  description?: string
  breadcrumbs?: PageBreadcrumbItem[]
  back?: { label: string; to?: string; onClick?: () => void }
  actions?: ReactNode
  belowHeader?: ReactNode
  hasAnalysis: boolean
  analysisPanel: ReactNode
  analysisUnavailableRetry?: ReactNode
  secondaryPanel: ReactNode
}

export type MatchDetailShellProps =
  | (MatchDetailShellBaseProps & {
      mode: 'person'
      activeTab: PersonMatchDetailTab
      onTabChange: (tab: PersonMatchDetailTab) => void
      hasCoverLetter?: boolean
      letterPanel?: ReactNode
    })
  | (MatchDetailShellBaseProps & {
      mode: 'opportunity'
      activeTab: OpportunityMatchDetailTab
      onTabChange: (tab: OpportunityMatchDetailTab) => void
    })

function personTabItems(hasAnalysis: boolean, hasCoverLetter: boolean): PageLineTabItem[] {
  const items: PageLineTabItem[] = [
    {
      value: 'analysis',
      label: 'Analysis',
      icon: ChartNoAxesColumn,
      ...(hasAnalysis
        ? {}
        : {
            unavailableTitle: ANALYSIS_UNAVAILABLE_TITLE,
          }),
    },
  ]
  if (hasCoverLetter) {
    items.push({
      value: 'letter',
      label: 'Cover letter',
      icon: FileText,
    })
  }
  items.push({
    value: 'profile',
    label: 'Profile',
    icon: User,
  })
  return items
}

function opportunityTabItems(hasAnalysis: boolean): PageLineTabItem[] {
  return [
    {
      value: 'analysis',
      label: 'Analysis',
      icon: ChartNoAxesColumn,
      ...(hasAnalysis
        ? {}
        : {
            unavailableTitle: ANALYSIS_UNAVAILABLE_TITLE,
          }),
    },
    {
      value: 'job',
      label: 'Job',
      icon: Briefcase,
    },
  ]
}

export function MatchDetailShell(props: MatchDetailShellProps) {
  const {
    title,
    description,
    breadcrumbs,
    back,
    actions,
    belowHeader,
    activeTab,
    onTabChange,
    hasAnalysis,
    analysisUnavailableRetry,
    mode,
    analysisPanel,
    secondaryPanel,
  } = props
  const hasCoverLetter = mode === 'person' ? (props.hasCoverLetter ?? false) : false
  const letterPanel = mode === 'person' ? props.letterPanel : null
  const items =
    mode === 'person'
      ? personTabItems(hasAnalysis, hasCoverLetter)
      : opportunityTabItems(hasAnalysis)

  return (
    <PageContainer>
      <div>
        <PageHeaderStack compact>
          <PageHeader
            leading={
              <PageHeaderLeading breadcrumbs={breadcrumbs} back={back} />
            }
            title={title}
            description={description}
            actions={actions}
          />
          <PageLineTabs
            value={activeTab}
            onValueChange={(value) => {
              if (mode === 'person') {
                onTabChange(value as PersonMatchDetailTab)
              } else {
                onTabChange(value as OpportunityMatchDetailTab)
              }
            }}
            ariaLabel={mode === 'person' ? 'Candidate sections' : 'Match sections'}
            items={items}
            renderTrigger={(item, defaultTrigger) => {
              if (item.unavailableTitle) {
                return (
                  <UnavailableLineTabTrigger
                    value={item.value}
                    label={item.ariaLabel ?? String(item.label)}
                    unavailableTitle={item.unavailableTitle}
                  >
                    {item.icon ? <item.icon className="h-4 w-4" aria-hidden /> : null}
                    {item.label}
                  </UnavailableLineTabTrigger>
                )
              }
              return defaultTrigger
            }}
          />
        </PageHeaderStack>

        {belowHeader ? <div className="mb-6">{belowHeader}</div> : null}

        {mode === 'person' ? (
          activeTab === 'analysis' && hasAnalysis ? (
            analysisPanel
          ) : activeTab === 'analysis' && !hasAnalysis ? (
            <AnalysisUnavailablePanel retry={analysisUnavailableRetry} />
          ) : activeTab === 'letter' && hasCoverLetter ? (
            letterPanel
          ) : (
            secondaryPanel
          )
        ) : activeTab === 'analysis' && hasAnalysis ? (
          analysisPanel
        ) : activeTab === 'analysis' && !hasAnalysis ? (
          <AnalysisUnavailablePanel retry={analysisUnavailableRetry} />
        ) : (
          secondaryPanel
        )}
      </div>
    </PageContainer>
  )
}
